// src/render/shrimpRenderer.ts

import { drawEye } from './eyeRenderer';

type ShrimpEntity = {
  id?: string;
  size?: number;
  colorHue?: number;
  patternType?: string;
  eyeType?: string;
  bodySlimness?: number;
  _shrimpPhase?: number;
};

const SEGMENT_COUNT = 4;
const ANTENNA_SCALE = 1.5;
const CLAW_SCALE = 0.26;
const TAIL_SCALE = 0.28;

export function drawShrimp(
  f: ShrimpEntity,
  ctx: CanvasRenderingContext2D,
  timeMs: number = typeof performance !== 'undefined' ? performance.now() : Date.now()
) {
  const size = typeof f.size === 'number' ? f.size : 8;
  const bodySlimness = clamp(typeof f.bodySlimness === 'number' ? f.bodySlimness : 0.9, 0.6, 1.2);
  const hue = typeof f.colorHue === 'number' ? f.colorHue : 0;
  const bodyLen = clamp(size * 2.1, 18, 120);
  const bodyHt = clamp(size * 0.75, 8, 50) * bodySlimness;
  const phase = (timeMs * 0.006 + seeded(String(f.id ?? 'shrimp'), 11) * Math.PI * 2) % (Math.PI * 2);
  const wiggle = Math.sin(phase);
  const probe = Math.sin(phase * 1.6);

  const base = `hsl(${hue} 70% 50%)`;
  const lighter = `hsl(${hue} 75% 65%)`;
  const darker = `hsl(${(hue + 330) % 360} 70% 32%)`;
  const accent = `hsl(${(hue + 30) % 360} 80% 55%)`;
  const white = '#fff';

  const segSpacing = bodyLen * 0.18;
  const headX = bodyLen * 0.35;
  const startX = headX - segSpacing * (SEGMENT_COUNT - 1);

  // Tail fan
  const tailX = startX - segSpacing * 0.75;
  const tailW = bodyHt * (1.2 + 0.2 * wiggle);
  const tailL = bodyLen * TAIL_SCALE;
  ctx.fillStyle = lighter;
  ctx.beginPath();
  ctx.moveTo(tailX, 0);
  ctx.quadraticCurveTo(tailX - tailL * 0.45, -tailW * 0.85, tailX - tailL, 0);
  ctx.quadraticCurveTo(tailX - tailL * 0.45, tailW * 0.85, tailX, 0);
  ctx.fill();
  // DEBUG: tail marker
  ctx.save();
  ctx.strokeStyle = 'red';
  ctx.lineWidth = 2;
  ctx.beginPath();
  ctx.arc(tailX - tailL * 0.6, 0, Math.max(3, bodyHt * 0.2), 0, Math.PI * 2);
  ctx.stroke();
  ctx.restore();

  // Segments + head
  for (let i = 0; i < SEGMENT_COUNT; i += 1) {
    const x = startX + i * segSpacing;
    const rx = bodyLen * (0.18 - i * 0.012);
    const ry = bodyHt * 0.35;
    const fill = patternFill(f.patternType, base, lighter, darker, accent, i);
    ctx.fillStyle = fill;
    ctx.beginPath();
    ctx.ellipse(x, 0, rx, ry, 0, 0, Math.PI * 2);
    ctx.fill();
  }

  const headRx = bodyLen * 0.2;
  const headRy = bodyHt * 0.42;
  ctx.fillStyle = patternFill(f.patternType, base, lighter, darker, accent, SEGMENT_COUNT);
  ctx.beginPath();
  ctx.ellipse(headX + headRx * 0.25, 0, headRx, headRy, 0, 0, Math.PI * 2);
  ctx.fill();

  // Patterns overlay
  if (f.patternType === 'spots') {
    drawSpotPattern(ctx, f.id, bodyLen, bodyHt, white);
  } else if (f.patternType === 'gradient') {
    const grd = ctx.createLinearGradient(startX - segSpacing, 0, headX + headRx, 0);
    grd.addColorStop(0, lighter);
    grd.addColorStop(1, base);
    ctx.fillStyle = grd;
    ctx.globalAlpha = 0.35;
    ctx.beginPath();
    ctx.ellipse(headX + headRx * 0.15, 0, headRx * 1.1, headRy * 1.05, 0, 0, Math.PI * 2);
    ctx.fill();
    ctx.globalAlpha = 1;
  }

  // Legs
  ctx.strokeStyle = white;
  ctx.lineWidth = Math.max(1, bodyHt * 0.08);
  for (let i = 0; i < SEGMENT_COUNT; i += 1) {
    const x = startX + i * segSpacing;
    for (let j = -1; j <= 1; j += 1) {
      const lx = x - bodyLen * 0.05;
      const ly = bodyHt * 0.2 + j * bodyHt * 0.08;
      ctx.beginPath();
      ctx.moveTo(lx, ly);
      ctx.lineTo(lx + bodyLen * 0.08, ly + bodyHt * 0.35);
      ctx.stroke();
    }
  }

  // Claws
  const clawX = headX + headRx * 0.8;
  const clawLen = bodyLen * CLAW_SCALE;
  ctx.strokeStyle = base;
  ctx.lineWidth = Math.max(1.5, bodyHt * 0.14);
  ctx.beginPath();
  ctx.moveTo(clawX, -bodyHt * 0.1);
  ctx.lineTo(clawX + clawLen, -bodyHt * 0.22);
  ctx.moveTo(clawX, bodyHt * 0.1);
  ctx.lineTo(clawX + clawLen, bodyHt * 0.22);
  ctx.stroke();
  ctx.fillStyle = base;
  ctx.beginPath();
  ctx.moveTo(clawX + clawLen, -bodyHt * 0.22);
  ctx.lineTo(clawX + clawLen * 1.15, -bodyHt * 0.3);
  ctx.lineTo(clawX + clawLen * 1.05, -bodyHt * 0.12);
  ctx.closePath();
  ctx.fill();
  ctx.beginPath();
  ctx.moveTo(clawX + clawLen, bodyHt * 0.22);
  ctx.lineTo(clawX + clawLen * 1.15, bodyHt * 0.3);
  ctx.lineTo(clawX + clawLen * 1.05, bodyHt * 0.12);
  ctx.closePath();
  ctx.fill();

  // Antennae
  const antLen = bodyLen * ANTENNA_SCALE * (1 + 0.15 * probe);
  ctx.strokeStyle = white;
  ctx.lineWidth = Math.max(1.2, bodyHt * 0.07);
  ctx.beginPath();
  const antLift = bodyHt * (0.65 + 0.25 * probe);
  const antBend = antLen * (0.25 + 0.12 * probe);
  ctx.moveTo(headX + headRx, -bodyHt * 0.1);
  ctx.quadraticCurveTo(
    headX + antBend,
    -antLift,
    headX + antLen,
    -antLift - bodyHt * 0.2
  );
  ctx.moveTo(headX + headRx, bodyHt * 0.1);
  ctx.quadraticCurveTo(
    headX + antBend,
    antLift,
    headX + antLen,
    antLift + bodyHt * 0.2
  );
  ctx.stroke();

  // Eyes (two)
  const eyeX = headX + headRx * 0.25;
  const eyeY = -headRy * 0.35;
  drawEye({
    ctx,
    eyeType: f.eyeType,
    x: eyeX,
    y: eyeY,
    bodyLen,
    bodyHt,
    size,
    idSeed: f.id,
    colorHue: hue,
  });
  drawEye({
    ctx,
    eyeType: f.eyeType,
    x: eyeX,
    y: -eyeY,
    bodyLen,
    bodyHt,
    size,
    idSeed: f.id ? `${f.id}-r` : undefined,
    colorHue: hue,
  });
}

function patternFill(
  patternType: string | undefined,
  base: string,
  lighter: string,
  darker: string,
  accent: string,
  index: number
) {
  if (patternType === 'stripes') {
    return index % 2 === 0 ? base : accent;
  }
  if (patternType === 'gradient') {
    return index % 2 === 0 ? base : lighter;
  }
  if (patternType === 'spots') {
    return base;
  }
  return patternType === 'solid' ? base : base;
}

function drawSpotPattern(
  ctx: CanvasRenderingContext2D,
  id: string | undefined,
  bodyLen: number,
  bodyHt: number,
  spotColor: string
) {
  const seed = id || 'shrimp';
  ctx.fillStyle = spotColor;
  for (let i = 0; i < 6; i += 1) {
    const rx = seeded(seed, i * 2) * bodyLen * 0.4 - bodyLen * 0.2;
    const ry = seeded(seed, i * 2 + 1) * bodyHt * 0.7 - bodyHt * 0.35;
    const r = Math.max(2, bodyHt * (0.12 + seeded(seed, i + 5) * 0.1));
    ctx.beginPath();
    ctx.arc(rx, ry, r, 0, Math.PI * 2);
    ctx.fill();
  }
}

function seeded(seed: string, index: number) {
  const str = `${seed}:${index}`;
  let hash = 2166136261;
  for (let i = 0; i < str.length; i++) {
    hash ^= str.charCodeAt(i);
    hash = Math.imul(hash, 16777619);
  }
  return (hash >>> 0) / 4294967295;
}

function clamp(value: number, min: number, max: number) {
  return Math.max(min, Math.min(max, value));
}
